import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../../../routes';
import CharacterBasicInfo from './CharacterBasicInfo';
import CharacterStats from './CharacterStats';
import CharacterAptitudes from './CharacterAptitudes';
import CharacterCostumes from './CharacterCostumes';
import { API_BASE_URL } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import authService from '../../../services/AuthService';

const CharacterEdit = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, loading: authLoading } = useAuth();
    const isUserAdmin = isAdmin();

    // Check if we are creating a new character
    const isNew = location.pathname === `${ROUTES.CHARACTER}/new`;

    // Redirect non-admins
    useEffect(() => {
        if (!authLoading && !isUserAdmin) {
            navigate(ROUTES.CHARACTERS_ORIGINAL);
        }
    }, [authLoading, isUserAdmin, navigate]);

    // State for character data
    const [character, setCharacter] = useState(null);
    const [stats, setStats] = useState([]);
    const [aptitudes, setAptitudes] = useState(null);
    const [costumes, setCostumes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCostume, setSelectedCostume] = useState(null);

    // Global state for new character creation
    const [newCharData, setNewCharData] = useState({
        basic: null,
        stats: null,
        aptitudes: null,
        costume: null
    });

    useEffect(() => {
        if (id && !isNew && isUserAdmin) {
            fetchCharacterData(id);
        }
    }, [id, isNew, isUserAdmin]);

    const fetchCharacterData = async (charId) => {
        try {
            setLoading(true);
            // Fetch all data in parallel
            const [charsRes, statsRes, aptRes, cardsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/characters`),
                fetch(`${API_BASE_URL}/stats`),
                fetch(`${API_BASE_URL}/aptitudes`),
                fetch(`${API_BASE_URL}/cards`)
            ]);

            const charsData = await charsRes.json();
            const statsData = await statsRes.json();
            const aptData = await aptRes.json();
            const cardsData = await cardsRes.json();

            // Filter for specific character
            const char = charsData.find(c => c.Id_Personaje.toString() === charId);
            const charStats = statsData.filter(s => s.Id_Personaje.toString() === charId);
            // Changed from find to filter to get all matching aptitudes
            const charApt = aptData.filter(a => a.Id_Personaje.toString() === charId);
            const charCards = cardsData.filter(c => c.Id_Personaje.toString() === charId);

            setCharacter(char);
            setStats(charStats);
            setAptitudes(charApt);
            setCostumes(charCards);

            // Select first costume by default if available
            if (charCards.length > 0) {
                setSelectedCostume(charCards[0]);
            }

        } catch (error) {
            console.error('Error fetching character data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle global save for new character
    const handleGlobalSave = async () => {
        if (!newCharData.basic || !newCharData.stats || !newCharData.aptitudes || !newCharData.costume) {
            alert(t('CharacterEdit.alert_complete_fields'));
            return;
        }

        try {
            setLoading(true);

            // 1. Create Character
            const charRes = await fetch(`${API_BASE_URL}/characters`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify(newCharData.basic)
            });

            if (!charRes.ok) throw new Error(t('CharacterEdit.error_create'));
            const charResult = await charRes.json();
            const newId = charResult.character.Id_Personaje;

            // 2. Create First Costume
            const costumePayload = {
                ...newCharData.costume,
                Id_Personaje: newId
            };
            const cardRes = await fetch(`${API_BASE_URL}/cards`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify(costumePayload)
            });
            const cardResult = await cardRes.json();
            const newCardId = cardResult.card.card_id;

            // 3. Create Stats (Batch)
            // Stats need card_id now
            const statsPayload = {
                Id_Personaje: newId,
                card_id: newCardId,
                stats: newCharData.stats
            };
            await fetch(`${API_BASE_URL}/stats`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify(statsPayload)
            });

            // 4. Create Aptitudes
            // Aptitudes need card_id now
            const aptPayload = {
                ...newCharData.aptitudes,
                Id_Personaje: newId,
                card_id: newCardId
            };
            await fetch(`${API_BASE_URL}/aptitudes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify(aptPayload)
            });

            alert(t('CharacterEdit.alert_success'));
            navigate('/'); // Return to list

        } catch (error) {
            console.error('Error creating character:', error);
            alert(t('CharacterEdit.error_create') + ': ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <div className="min-h-screen bg-panel-gray pt-20 flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-green"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-panel-gray pt-20 pb-24">
            <div className="max-w-4xl mx-auto p-6">
                {/* Header with Back Button */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">
                                {isNew
                                    ? t('CharacterEdit.header_create')
                                    : t('CharacterEdit.header_edit', { name: character?.name_en || t('CharacterEdit.loading') })
                                }
                            </h1>
                            <p className="text-gray-600">
                                {isNew
                                    ? t('CharacterEdit.subtitle_create')
                                    : t('CharacterEdit.subtitle_edit')
                                }
                            </p>
                        </div>
                    </div>

                    {/* Global Save Button for New Character */}
                    {isNew && (
                        <button
                            onClick={handleGlobalSave}
                            disabled={loading}
                            className={`px-6 py-3 bg-primary-green text-white rounded-xl font-bold shadow-lg hover:bg-green-600 transition-all transform hover:scale-105 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                            {loading ? t('CharacterEdit.button_creating') : t('CharacterEdit.button_create_full')}
                        </button>
                    )}
                </div>

                {loading && !isNew ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-green mx-auto"></div>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* 1. Basic Info */}
                        <section>
                            <CharacterBasicInfo
                                character={character}
                                isNew={isNew}
                                onSave={(data) => isNew ? setNewCharData(prev => ({ ...prev, basic: data })) : fetchCharacterData(id)}
                            />
                        </section>

                        {/* 2. Costumes */}
                        <section>
                            <CharacterCostumes
                                characterId={id}
                                costumes={costumes}
                                isNew={isNew}
                                selectedCostume={selectedCostume}
                                onSelectCostume={setSelectedCostume}
                                onSave={(data) => {
                                    if (isNew) {
                                        setNewCharData(prev => ({ ...prev, costume: data }));
                                        setSelectedCostume(data); // Set as selected to enable Stats editing
                                    } else {
                                        fetchCharacterData(id);
                                    }
                                }}
                            />
                        </section>

                        {/* 3. Stats (Dependent on Costume) */}
                        <section>
                            <CharacterStats
                                characterId={id}
                                selectedCostume={selectedCostume}
                                initialStats={isNew ? [] : stats.filter(s => s.card_id === selectedCostume?.card_id)}
                                isNew={isNew}
                                onSave={(data) => isNew ? setNewCharData(prev => ({ ...prev, stats: data })) : fetchCharacterData(id)}
                            />
                        </section>

                        {/* 4. Aptitudes */}
                        <section>
                            <CharacterAptitudes
                                characterId={id}
                                cardId={selectedCostume?.card_id}
                                initialAptitudes={isNew ? null : (aptitudes ? aptitudes.find(a => a.card_id === selectedCostume?.card_id) : null)}
                                isNew={isNew}
                                onSave={(data) => isNew ? setNewCharData(prev => ({ ...prev, aptitudes: data })) : fetchCharacterData(id)}
                            />
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CharacterEdit;
// Revisado: 2025-12-16
